// Ausbildungsnachweis math, shared with the server: everything below this header is identical to
// utils/apprenticeship/calc.js (test/apprenticeship/calc.test.js compares them).

const PLACES = ["BETRIEB", "SCHULE", "UEBERBETRIEBLICH", "URLAUB", "KRANK", "FEIERTAG", "FREI"];
const REPORT_STATUSES = ["draft", "submitted", "returned", "signed"];
// A week is done once the apprentice handed it in; drafts and returned weeks still count as open
const DONE_STATUSES = ["submitted", "signed"];
const MAX_ACTIVITIES_PER_DAY = 20;
const MAX_DAY_MINUTES = 24 * 60;
// ArbZG § 3 (adults) — and JArbSchG § 8 allows only 8 h for minors
const LONG_DAY_MINUTES = 10 * 60;
const DAY_MS = 86400000;

const isDay = value => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const time = Date.parse(value + "T00:00:00Z");
  return !Number.isNaN(time) && new Date(time).toISOString().slice(0, 10) === value;
};

const toUtc = day => Date.parse(day + "T00:00:00Z");
const fromUtc = time => new Date(time).toISOString().slice(0, 10);
const addDays = (day, days) => fromUtc(toUtc(day) + days * DAY_MS);

/** ISO weekday: 1 = Monday … 7 = Sunday. */
const isoWeekday = day => {
  const d = new Date(toUtc(day)).getUTCDay();
  return d === 0 ? 7 : d;
};

const mondayOf = day => addDays(day, 1 - isoWeekday(day));

/** ISO 8601 week of a day: the week belongs to the year that holds its Thursday. */
const isoWeek = day => {
  const thursday = addDays(day, 4 - isoWeekday(day));
  const year = Number(thursday.slice(0, 4));
  const week = Math.floor((toUtc(thursday) - toUtc(year + "-01-01")) / DAY_MS / 7) + 1;
  return { year, week };
};

/** "2026-W05" */
const weekLabel = day => {
  const { year, week } = isoWeek(day);
  return year + "-W" + String(week).padStart(2, "0");
};

/** Monday of ISO week `week` of `year` (4 January is always in week 1). */
const mondayOfIsoWeek = (year, week) => addDays(mondayOf(year + "-01-04"), (week - 1) * 7);

/** The seven days Monday–Sunday of the week starting on `monday`. */
const weekDays = monday => [0, 1, 2, 3, 4, 5, 6].map(i => addDays(monday, i));

/**
 * Ausbildungsjahr of a day: 1 in the first twelve months from the start of training, 2 in the
 * next twelve, …; 0 before the start.
 */
const trainingYear = (startDate, day) => {
  if (!isDay(startDate || "") || !isDay(day || "") || day < startDate) return 0;
  const [sy, sm, sd] = startDate.split("-").map(Number);
  const [y, m, d] = day.split("-").map(Number);
  const months = (y - sy) * 12 + (m - sm) - (d < sd ? 1 : 0);
  return Math.floor(months / 12) + 1;
};

/** Hours as typed ("7,5", "7.5", "7:30", "7") → minutes; null when unreadable. */
const parseHours = value => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number")
    return Number.isFinite(value) && value >= 0 ? Math.round(value * 60) : null;
  const text = String(value).trim();
  if (!text) return null;
  const clock = text.match(/^(\d{1,2}):([0-5]\d)$/);
  if (clock) return Number(clock[1]) * 60 + Number(clock[2]);
  const decimal = text.replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(decimal)) return null;
  return Math.round(Number(decimal) * 60);
};

/** Minutes → "7:30" */
const formatMinutes = minutes => {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  return Math.floor(m / 60) + ":" + String(m % 60).padStart(2, "0");
};

/** Default place of a day: weekend free, Berufsschule on the school weekdays, else Betrieb. */
const defaultPlace = (day, schoolDays) => {
  const weekday = isoWeekday(day);
  if (weekday >= 6) return "FREI";
  return (schoolDays || []).map(Number).includes(weekday) ? "SCHULE" : "BETRIEB";
};

const clip = (value, max) =>
  String(value === null || value === undefined ? "" : value)
    .trim()
    .slice(0, max);

const cleanMinutes = value => {
  const n = Math.round(Number(value));
  return Number.isFinite(n) && n > 0 ? Math.min(n, MAX_DAY_MINUTES) : 0;
};

/** One activity line { text, minutes }. */
const normalizeActivity = raw => ({
  text: clip(raw && raw.text, 2000),
  minutes: cleanMinutes(raw && raw.minutes)
});

/**
 * The seven days of the week starting on `monday`, from what the client sent: unknown dates
 * are dropped, missing days get their default place, empty activity lines go.
 */
const normalizeDays = (monday, rawDays, schoolDays) => {
  const byDate = {};
  (Array.isArray(rawDays) ? rawDays : []).forEach(raw => {
    if (raw && isDay(raw.date || "")) byDate[raw.date] = raw;
  });
  return weekDays(monday).map(date => {
    const raw = byDate[date] || {};
    const activities = (Array.isArray(raw.activities) ? raw.activities : [])
      .slice(0, MAX_ACTIVITIES_PER_DAY)
      .map(normalizeActivity)
      .filter(a => a.text || a.minutes);
    return {
      date,
      place: PLACES.includes(raw.place) ? raw.place : defaultPlace(date, schoolDays),
      activities
    };
  });
};

/**
 * Totals of a week: minutes per day and per place, Unterweisungen, and warnings
 * (EMPTY_DAY: a Betrieb/Schule day without an entry; LONG_DAY: over 10 h; NOTHING: no entry).
 */
const summarizeWeek = report => {
  const days = (report && report.days) || [];
  const byPlace = {};
  PLACES.forEach(place => {
    byPlace[place] = 0;
  });
  const warnings = [];
  const perDay = days.map(day => {
    const minutes = (day.activities || []).reduce((sum, a) => sum + cleanMinutes(a.minutes), 0);
    byPlace[day.place] = (byPlace[day.place] || 0) + minutes;
    if (
      (day.place === "BETRIEB" || day.place === "SCHULE" || day.place === "UEBERBETRIEBLICH") &&
      !(day.activities || []).some(a => a.text)
    ) {
      warnings.push({ code: "EMPTY_DAY", date: day.date });
    }
    if (minutes > LONG_DAY_MINUTES) warnings.push({ code: "LONG_DAY", date: day.date });
    return { date: day.date, place: day.place, minutes };
  });
  const instructionsMinutes = cleanMinutes(
    report && report.instructions && report.instructions.minutes
  );
  const totalMinutes = perDay.reduce((sum, d) => sum + d.minutes, 0) + instructionsMinutes;
  if (!totalMinutes && !perDay.some((d, i) => (days[i].activities || []).some(a => a.text))) {
    warnings.unshift({ code: "NOTHING", date: "" });
  }
  return {
    perDay,
    byPlace,
    workMinutes: byPlace.BETRIEB + byPlace.UEBERBETRIEBLICH,
    schoolMinutes: byPlace.SCHULE,
    instructionsMinutes,
    totalMinutes,
    warnings
  };
};

/**
 * Weeks the apprentice still owes: every week from the start of training up to the last
 * finished week before `asOf` (the running week is not due yet), capped by the end of
 * training, that has no handed-in report. `reports` [{ weekStart, status }].
 * → [{ weekStart, label, state: "missing" | "draft" | "returned" }], oldest first.
 */
const missingWeeks = ({ startDate, endDate, asOf, reports }) => {
  if (!isDay(startDate || "") || !isDay(asOf || "")) return [];
  const status = {};
  (reports || []).forEach(r => {
    status[r.weekStart] = r.status;
  });
  const lastMonday = addDays(mondayOf(asOf), -7);
  const endMonday = isDay(endDate || "") ? mondayOf(endDate) : lastMonday;
  const until = endMonday < lastMonday ? endMonday : lastMonday;
  const out = [];
  for (let monday = mondayOf(startDate); monday <= until; monday = addDays(monday, 7)) {
    const state = status[monday];
    if (DONE_STATUSES.indexOf(state) >= 0) continue;
    out.push({ weekStart: monday, label: weekLabel(monday), state: state || "missing" });
  }
  return out;
};

export {
  PLACES,
  REPORT_STATUSES,
  DONE_STATUSES,
  MAX_ACTIVITIES_PER_DAY,
  LONG_DAY_MINUTES,
  isDay,
  addDays,
  isoWeekday,
  mondayOf,
  isoWeek,
  weekLabel,
  mondayOfIsoWeek,
  weekDays,
  trainingYear,
  parseHours,
  formatMinutes,
  defaultPlace,
  normalizeActivity,
  normalizeDays,
  summarizeWeek,
  missingWeeks
};
